/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LmsShell, studentApi, setStudentSession } from "./portal/shell";

/* --------------------------------------------------------------------
   School-ID login + first-time self-registration for bulk/offline
   licences sold to First Nations schools and other organisations.
   NO email required. Students use:
     School ID + first name + last name + password
   The toggle at the top switches between "First time (claim seat)" and
   "Returning (sign in)". Opaque 404 messages are returned by the
   backend for every credential-failure flavor so this page can show a
   single generic error.
   -------------------------------------------------------------------- */
export default function SchoolLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("register");    // "register" | "login"
  const [schoolId, setSchoolId] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = {
      school_id: schoolId.trim().toUpperCase(),
      first_name: first.trim(),
      last_name: last.trim(),
      password: pw,
    };
    if (payload.school_id.length < 4) return setErr("Enter the School ID your teacher gave you.");
    if (!payload.first_name || !payload.last_name) return setErr("Enter your first and last name.");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (mode === "register" && pw !== pw2) return setErr("Passwords don't match.");

    setBusy(true);
    try {
      const data = mode === "register"
        ? await studentApi.schoolRegister(payload)
        : await studentApi.schoolLogin(payload);
      setStudentSession(data);
      nav("/dashboard", { replace: true });
    } catch (ex) {
      setErr(ex.message || "Login details not found.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <LmsShell subtitle="School login">
      <form className="tbg-lms-card" onSubmit={submit} data-testid="school-login-form">
        <div className="h-sub">Group licence · {mode === "register" ? "First time" : "Returning student"}</div>
        <h2>{mode === "register" ? "Claim your seat" : "Welcome back"}</h2>
        <p>
          {mode === "register"
            ? "Enter the School ID your teacher gave you, your name, and create a password."
            : "Use the same School ID, name, and password you created last time."}
        </p>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[
            { k: "register", label: "First time" },
            { k: "login", label: "Returning student" },
          ].map(({ k, label }) => (
            <button key={k} type="button"
              onClick={() => { setMode(k); setErr(""); }}
              data-testid={`school-login-mode-${k}`}
              style={{
                flex: 1,
                background: mode === k ? "#3dc0af" : "transparent",
                color: mode === k ? "#051210" : "#a5b8b3",
                border: "1px solid " + (mode === k ? "#3dc0af" : "#2a403c"),
                padding: "8px 12px", borderRadius: 999,
                font: "700 10px 'JetBrains Mono', monospace",
                letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer",
              }}>
              {label}
            </button>
          ))}
        </div>

        <label className="tbg-lms-lbl">School ID<span className="req">*</span></label>
        <input
          required value={schoolId}
          onChange={(e) => setSchoolId(e.target.value.toUpperCase())}
          className="tbg-lms-input" placeholder="e.g. ABCD2345"
          data-testid="school-login-id"
          autoCapitalize="characters" style={{ letterSpacing: "0.15em", fontFamily: "'JetBrains Mono', monospace" }}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label className="tbg-lms-lbl">First name<span className="req">*</span></label>
            <input required value={first} onChange={(e) => setFirst(e.target.value)}
              className="tbg-lms-input" placeholder="First"
              data-testid="school-login-first" />
          </div>
          <div>
            <label className="tbg-lms-lbl">Last name<span className="req">*</span></label>
            <input required value={last} onChange={(e) => setLast(e.target.value)}
              className="tbg-lms-input" placeholder="Last"
              data-testid="school-login-last" />
          </div>
        </div>

        <label className="tbg-lms-lbl">Password<span className="req">*</span></label>
        <input
          type="password" required minLength={mode === "register" ? 8 : 1}
          value={pw} onChange={(e) => setPw(e.target.value)}
          className="tbg-lms-input"
          placeholder={mode === "register" ? "Create a password (8+ characters)" : "Your password"}
          data-testid="school-login-pw"
        />
        {mode === "register" && (
          <>
            <label className="tbg-lms-lbl">Confirm password<span className="req">*</span></label>
            <input
              type="password" required minLength={8}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              className="tbg-lms-input" placeholder="Re-enter password"
              data-testid="school-login-pw2"
            />
          </>
        )}
        {err && <div className="tbg-lms-err" data-testid="school-login-error">{err}</div>}

        <button type="submit" className="tbg-lms-btn" disabled={busy} data-testid="school-login-submit">
          {busy
            ? (mode === "register" ? "Claiming seat…" : "Signing in…")
            : (mode === "register" ? "Claim my seat & start" : "Sign in")}
        </button>

        <div className="tbg-lms-foot" style={{ marginTop: 14 }}>
          <Link to="/login">Have an email account? Log in</Link>
          <span style={{ color: "#7b8b87", fontSize: 11 }}>
            Forgot password? Ask your teacher or administrator to reset it.
          </span>
        </div>
      </form>
    </LmsShell>
  );
}
